/**
 * Parser for `browser_network_requests` text output.
 *
 * The actual noise/dedupe/cap filtering happens server-side now — applied in
 * `nlp_automation_phase2/src/testReplayAgent.js` (Replay + Launch via Lambda)
 * and `sessionLogger.js` (AI Generate). What the frontend receives is already
 * filtered, so we only need to parse it for rendering.
 *
 * Two on-the-wire formats are supported because data persisted at different
 * times has different shapes:
 *   - Bracketed (raw MCP):      `[GET] https://host/path => [200] OK`
 *   - Plain (post-filter):      `GET /path => 200`
 */

const RX_BRACKET_STATUS = /^\[(GET|POST|PUT|DELETE|PATCH|OPTIONS|HEAD|WS)\]\s+(.+?)\s*=>\s*\[(\d+)\].*$/;
const RX_BRACKET_FAILED = /^\[(GET|POST|PUT|DELETE|PATCH|OPTIONS|HEAD|WS)\]\s+(.+?)\s*=>\s*\[FAILED\].*$/i;
const RX_BRACKET_PENDING = /^\[(GET|POST|PUT|DELETE|PATCH|OPTIONS|HEAD|WS)\]\s+(.+)$/;
const RX_PLAIN = /^(GET|POST|PUT|DELETE|PATCH|OPTIONS|HEAD|WS)\s+(.+?)\s*=>\s*(\d+|FAILED|pending)?$/i;
const RX_BODY = /^(?:Body|Request body):\s*(.+)$/i;

const stripHostAndQuery = (url) => url.replace(/^https?:\/\/[^/]+/, '').replace(/\?.*$/, '');

/**
 * Parses raw network text into row objects for the UI table.
 * Returns: Array<{ method, url, pathOnly, status, failed, body }>
 */
export function parseNetworkRequestsForDisplay(rawText) {
    if (!rawText || typeof rawText !== 'string') return [];
    const out = [];
    for (const raw of rawText.split('\n')) {
        const line = raw.trim();
        if (!line) continue;

        let m = line.match(RX_BRACKET_STATUS);
        if (m) {
            const url = m[2].trim();
            out.push({ method: m[1], url, pathOnly: stripHostAndQuery(url), status: parseInt(m[3], 10), failed: false, body: null });
            continue;
        }
        m = line.match(RX_BRACKET_FAILED);
        if (m) {
            const url = m[2].trim();
            out.push({ method: m[1], url, pathOnly: stripHostAndQuery(url), status: null, failed: true, body: null });
            continue;
        }
        m = line.match(RX_BRACKET_PENDING);
        if (m) {
            const url = m[2].trim();
            out.push({ method: m[1], url, pathOnly: stripHostAndQuery(url), status: null, failed: false, body: null });
            continue;
        }
        m = line.match(RX_PLAIN);
        if (m) {
            const url = m[2].trim();
            let status = null;
            let failed = false;
            if (m[3]) {
                if (/^\d+$/.test(m[3])) status = parseInt(m[3], 10);
                else if (/^FAILED$/i.test(m[3])) failed = true;
            }
            out.push({ method: m[1].toUpperCase(), url, pathOnly: stripHostAndQuery(url), status, failed, body: null });
            continue;
        }
        const bm = line.match(RX_BODY);
        if (bm && out.length > 0) {
            out[out.length - 1].body = bm[1];
        }
    }
    return out;
}
